// admin-devlog.js - the system Development Log for admins.
// Reads /api/admin/devlog (AdminDevLogController + GitHubChangelogService) and shows the
// FULL GitHub check-in feed (every commit), grouped by date with conventional-commit type
// badges. Same GitHub plumbing the campaign/changelog system uses, without the email.
// Mirrors admin-email-log.js. Read-only, admin-only.

import { getUser } from './auth.js';

function token() { return localStorage.getItem('gdbs_token') || ''; }

async function api(method, path) {
  const headers = { 'Content-Type': 'application/json' };
  if (token()) headers['Authorization'] = 'Bearer ' + token();
  const res = await fetch(path, { method, headers });
  if (!res.ok) throw new Error(`${method} ${path}: ${res.status}`);
  return res.json();
}

function esc(s) {
  if (s === null || s === undefined) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[c]));
}

// conventional-commit type -> colour. Anything unknown falls back to 'other'.
const TYPE_COLOR = {
  feat: '#10b981', fix: '#ef4444', perf: '#3b82f6', refactor: '#8b5cf6',
  docs: '#64748b', build: '#f59e0b', ci: '#f59e0b', test: '#14b8a6',
  chore: '#64748b', style: '#a78bfa', revert: '#ef4444', other: '#94a3b8',
};

function typeBadge(t) {
  const c = TYPE_COLOR[t] || TYPE_COLOR.other;
  return `<span style="display:inline-block;min-width:62px;text-align:center;padding:1px 8px;border-radius:10px;` +
    `font-size:10px;font-weight:700;border:1px solid ${c};color:${c};background:${c}1a;">${esc(t)}</span>`;
}

function dayLabel(iso) {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  return isNaN(d) ? esc(iso) : d.toISOString().slice(0, 10);
}
function timeLabel(iso) {
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toISOString().slice(11, 16);
}

let _loaded = false;

async function refresh() {
  // /api/admin/devlog is admin-only; bail otherwise (also covers the pre-login boot window).
  if (getUser()?.role !== 'admin') return;
  const status = document.getElementById('devlog-status');
  const feed = document.getElementById('devlog-feed');
  if (!feed) return;
  const typeFilter = document.getElementById('devlog-type')?.value || 'all';
  if (status) status.innerHTML = '<i class="fas fa-spinner fa-spin"></i> loading...';
  feed.innerHTML = '<p style="color:var(--text-dim);">Loading...</p>';
  try {
    const res = await api('GET', '/api/admin/devlog');
    _loaded = true;
    let commits = res.commits || [];
    if (!res.configured) {
      if (status) status.innerHTML = '<span style="color:#f59e0b;"><i class="fas fa-triangle-exclamation"></i> GitHub token not set</span>';
      feed.innerHTML = '<p style="color:var(--text-dim);font-size:13px;line-height:1.6;max-width:680px;">' +
        'The Development Log reads the repo\'s GitHub check-ins. Set <code>GitHub:Token</code> in the server secrets ' +
        '(a read-only fine-grained token for <code>' + esc(res.repo || 'the repo') + '</code>) to enable it. ' +
        'This is the same plumbing the changelog newsletter uses.</p>';
      return;
    }
    if (typeFilter !== 'all') commits = commits.filter(c => (c.type || 'other') === typeFilter);

    const when = res.cachedAt ? new Date(res.cachedAt) : null;
    if (status) status.innerHTML =
      `<i class="fas fa-check-circle" style="color:var(--accent,#10b981);"></i> ${commits.length} commit${commits.length === 1 ? '' : 's'}` +
      ` &middot; <span style="font-family:var(--mono,monospace);">${esc(res.repo)}</span>` +
      (when && !isNaN(when) ? ` &middot; synced ${esc(when.toISOString().slice(0, 16).replace('T', ' '))} UTC` : '');

    if (commits.length === 0) {
      feed.innerHTML = '<p style="color:var(--text-dim);">No commits' + (typeFilter !== 'all' ? ' of type "' + esc(typeFilter) + '"' : '') + '.</p>';
      return;
    }

    // group by day (commits arrive newest-first from the API)
    const groups = [];
    let cur = null;
    for (const c of commits) {
      const day = dayLabel(c.dateUtc);
      if (!cur || cur.day !== day) { cur = { day, items: [] }; groups.push(cur); }
      cur.items.push(c);
    }

    feed.innerHTML = groups.map(g => `
      <div style="margin:0 0 16px;">
        <div style="font-size:11px;font-weight:700;color:var(--text-dim);text-transform:uppercase;letter-spacing:0.05em;border-bottom:1px solid var(--border,#334155);padding-bottom:4px;margin-bottom:6px;">${esc(g.day)}</div>
        ${g.items.map(rowHtml).join('')}
      </div>`).join('');
  } catch (e) {
    if (status) status.innerHTML = `<span style="color:#ef4444;"><i class="fas fa-times-circle"></i> ${esc(e.message)}</span>`;
    feed.innerHTML = '<p style="color:var(--text-dim);">Could not load the development log.</p>';
  }
}

function rowHtml(c) {
  const scope = c.scope ? `<span style="color:#60a5fa;font-size:11px;">(${esc(c.scope)})</span> ` : '';
  const breaking = c.breaking ? '<span title="breaking change" style="color:#ef4444;font-weight:700;">!</span> ' : '';
  const ver = c.version ? `<span style="font-size:10px;color:#a78bfa;border:1px solid #a78bfa55;border-radius:9px;padding:0 6px;">${esc(c.version)}</span>` : '';
  const sha = c.url
    ? `<a href="${esc(c.url)}" target="_blank" rel="noopener" style="font-family:var(--mono,monospace);font-size:10px;color:var(--text-dim);" title="View on GitHub">${esc(c.sha)}</a>`
    : `<span style="font-family:var(--mono,monospace);font-size:10px;color:var(--text-dim);">${esc(c.sha)}</span>`;
  return `<div style="display:flex;align-items:baseline;gap:8px;padding:4px 0;flex-wrap:wrap;">
    ${typeBadge(c.type || 'other')}
    <span style="flex:1;min-width:240px;font-size:13px;color:var(--text,#e2e8f0);">${breaking}${scope}${esc(c.subject)}</span>
    ${ver}
    <span style="font-size:11px;color:var(--text-dim);white-space:nowrap;">${esc(c.author)}</span>
    <span style="font-size:11px;color:var(--text-dim);white-space:nowrap;">${timeLabel(c.dateUtc)}</span>
    ${sha}
  </div>`;
}

export function initAdminDevLog() {
  document.getElementById('btn-devlog-refresh')?.addEventListener('click', refresh);
  document.getElementById('devlog-type')?.addEventListener('change', refresh);
  // Load when the Dev Log subtab is opened (the panel is hidden at boot). Refresh on
  // every open so it stays current; the server cache is reused for ~5 min.
  const tab = document.querySelector('.atab[data-atab="devlog"]');
  if (tab) tab.addEventListener('click', () => setTimeout(refresh, 50));
}
